import type React from "react";
import type { reportingPeriod } from "@duress/shared";
import { fmtHours, fmtInt } from "@/lib/format";
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { useTeamDeviceBreakdown, type SelectedView } from "../periods/usePeriods";

type DeviceItem = reportingPeriod.TeamDeviceBreakdownItem;

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const hoursCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtHours(typeof v === "number" ? v : Number(v))}</span>
);

const deviceIdCell = (v: unknown): React.ReactNode =>
  typeof v === "string" && v.length > 0 ? v : <span className="text-muted-foreground">—</span>;

const DEVICE_COLUMNS: DataTableColumn<DeviceItem>[] = [
  { key: "watchName", label: "Device" },
  { key: "deviceId", label: "Device ID", render: deviceIdCell },
  { key: "deviceType", label: "Type" },
  { key: "sessions", label: "Sessions", render: numCell },
  { key: "totalHours", label: "Total Hours", render: hoursCell },
  { key: "lastSessionDate", label: "Last Session" },
];

interface Props {
  clientId: string;
  view: SelectedView | null;
  team: string | null;
  onOpenChange: (open: boolean) => void;
}

export function TeamDeviceBreakdownDialog({
  clientId,
  view,
  team,
  onOpenChange,
}: Props): React.JSX.Element | null {
  const periodId = view?.mode === "period" ? view.periodId : null;
  const { data, isLoading, error } = useTeamDeviceBreakdown(clientId, periodId, team);

  if (team === null) return null;

  return (
    <Dialog open onOpenChange={onOpenChange} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Devices — {team}</DialogTitle>
        <DialogDescription>
          Every device with a session row for this team this period, straight off the raw export —
          including micro-sessions that Team Performance's totals exclude.
        </DialogDescription>
      </DialogHeader>

      {view?.mode !== "period" ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Device details are only available for a single selected month — not a custom range or
          all-time view.
        </p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive py-8 text-center">
          Failed to load device data. Try again.
        </p>
      ) : !data || data.devices.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No sessions recorded for this team this period.
        </p>
      ) : (
        <DataTable<DeviceItem>
          columns={DEVICE_COLUMNS}
          data={data.devices}
          defaultSort={{ key: "totalHours", dir: "desc" }}
        />
      )}
    </Dialog>
  );
}
